import { Button, Input, Separator } from '@data-delivery/d3s';
import { useResetSftpUserPasswordMutation } from '@data-delivery/permission-ui-data-access-state';
import { CheckCircle, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { passwordCheckMessages, validateSftpUserPassword } from '../../utils';
import { showErrorToast } from '../../utils/error-utils';

export interface UserResetPasswordFormProps {
  userId: string;
  firstName?: string;
  lastName?: string;
  heading?: string;
  onCancel?: () => void;
  onSuccess?: () => void;
  disableNameChecks?: boolean;
}

export function UserResetPasswordForm({
  userId,
  firstName,
  lastName,
  heading = 'Reset Password',
  onCancel,
  onSuccess,
  disableNameChecks,
}: UserResetPasswordFormProps) {
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const resetPasswordMutation = useResetSftpUserPasswordMutation();
  const passwordResult = useMemo(() => validateSftpUserPassword(newPassword), [newPassword]);
  const nameChecks = useMemo(() => {
    if (disableNameChecks) {
      return { firstBlocked: false, lastBlocked: false };
    }

    const pwd = newPassword.toLowerCase();
    const f = (firstName || '').trim().toLowerCase();
    const l = (lastName || '').trim().toLowerCase();
    const firstBlocked = f.length > 0 && pwd.includes(f);
    const lastBlocked = l.length > 0 && pwd.includes(l);
    return { firstBlocked, lastBlocked };
  }, [newPassword, firstName, lastName, disableNameChecks]);

  return (
    <div className='flex flex-col'>
      {heading && <h2 className='text-lg font-semibold mb-2'>{heading}</h2>}
      <p className='text-sm text-muted-foreground mb-2'>
        Enter a new password for this user. All criteria must be met.
      </p>
      <Input
        type='password'
        autoFocus
        value={newPassword}
        placeholder='New password'
        onChange={e => {
          setNewPassword(e.target.value);
        }}
      />
      <Separator className='my-3' />
      <div className='space-y-1'>
        {passwordCheckMessages(passwordResult.checks).map(c => (
          <div key={c.key} className='flex items-center gap-2 text-xs'>
            {c.passed ? (
              <CheckCircle className='size-4 text-green-600' aria-label='Passed' />
            ) : (
              <XCircle className='size-4 text-muted-foreground' aria-label='Not passed' />
            )}
            <span className={c.passed ? 'text-green-700 dark:text-green-400' : ''}>{c.label}</span>
          </div>
        ))}
        {!disableNameChecks && (
          <>
            <div className='flex items-center gap-2 text-xs'>
              {nameChecks.firstBlocked ? (
                <XCircle
                  className='size-4 text-muted-foreground'
                  aria-label='Contains first name'
                />
              ) : (
                <CheckCircle className='size-4 text-green-600' aria-label='No first name' />
              )}
              <span className={nameChecks.firstBlocked ? '' : 'text-green-700 dark:text-green-400'}>
                Does not contain first name
              </span>
            </div>
            <div className='flex items-center gap-2 text-xs'>
              {nameChecks.lastBlocked ? (
                <XCircle className='size-4 text-muted-foreground' aria-label='Contains last name' />
              ) : (
                <CheckCircle className='size-4 text-green-600' aria-label='No last name' />
              )}
              <span className={nameChecks.lastBlocked ? '' : 'text-green-700 dark:text-green-400'}>
                Does not contain last name
              </span>
            </div>
          </>
        )}
      </div>
      <div className='flex w-full justify-end gap-2 mt-4'>
        {onCancel && (
          <Button
            variant='outline'
            size='sm'
            onClick={() => {
              setNewPassword('');
              onCancel();
            }}
            disabled={submitting || resetPasswordMutation.isPending}
          >
            Cancel
          </Button>
        )}
        <Button
          size='sm'
          onClick={async () => {
            if (!userId) {
              return;
            }

            setSubmitting(true);
            try {
              await resetPasswordMutation.mutateAsync({ userId, newPassword });
              setNewPassword('');
              toast.success('Password reset successfully');
              onSuccess?.();
            } catch (e) {
              showErrorToast(e);
            } finally {
              setSubmitting(false);
            }
          }}
          disabled={
            !passwordResult.valid ||
            nameChecks.firstBlocked ||
            nameChecks.lastBlocked ||
            submitting ||
            resetPasswordMutation.isPending ||
            !userId
          }
        >
          {submitting || resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
        </Button>
      </div>
    </div>
  );
}

export default UserResetPasswordForm;
